import { useEffect, useState } from "react";
import {
	Card,
	Row,
	Col,
	Typography,
	Divider,
	Table,
	Tag,
	Space,
	Descriptions,
	Spin,
	Alert,
	Button,
	Empty,
	Drawer,
	List,
	Avatar,
	notification,
	message,
} from "antd";
import {
	CalendarOutlined,
	EyeOutlined,
	ExportOutlined,
	InfoCircleOutlined,
	PayCircleOutlined,
	CloseOutlined,
	LinkOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
	requestGetInvoiceDetailsWithToken,
	requestPayInvoiceWithToken,
} from "../../../services/public.service";
import { requestGetPaymentList } from "../../../services/user.service";

const { Title, Text, Paragraph } = Typography;

function PublicInvoicePage() {
	const { token } = useParams();
	const [loading, setLoading] = useState(false);
	const [payments, setPayments] = useState([]);
	const [invoice, setInvoice] = useState(null);
	const [error, setError] = useState(null);
	const [paymentDrawerVisible, setPaymentDrawerVisible] = useState(false);
	const [selectedPayment, setSelectedPayment] = useState(null);
	const [timeRemaining, setTimeRemaining] = useState(null);
	const [isPaymentExpired, setIsPaymentExpired] = useState(false);

	useEffect(() => {
		document.title = "Faktur - Cloudiva";
	}, []);

	useEffect(() => {
		if (token) {
			fetchInvoiceWithToken();
		} else {
			setInvoice(null);
		}
		console.log("here");
	}, [token]);

	// Timer effect for payment expiration
	useEffect(() => {
		let interval;
		if (invoice?.payment?.expiredAt) {
			const updateTimer = () => {
				const now = new Date().getTime();
				const expiredAt = new Date(invoice.payment.expiredAt).getTime();
				const timeDiff = expiredAt - now;

				if (timeDiff > 0) {
					const hours = Math.floor(timeDiff / (1000 * 60 * 60));
					const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
					const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

					setTimeRemaining({
						hours,
						minutes,
						seconds,
						total: timeDiff,
					});
					setIsPaymentExpired(false);
				} else {
					setTimeRemaining(null);
					setIsPaymentExpired(true);
				}
			};

			updateTimer(); // Initial call
			interval = setInterval(updateTimer, 1000);
		} else {
			setTimeRemaining(null);
			setIsPaymentExpired(false);
		}

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [invoice?.payment?.expiredAt]);

	useEffect(() => {
		if (invoice) {
			fetchPaymentList(invoice.total, invoice.paymentMethods.join("|"));
		}
	}, [invoice]);

	const fetchInvoiceWithToken = () => {
		setLoading(true);
		requestGetInvoiceDetailsWithToken(token)
			.then((response) => {
				const inv = response.data.data;
				setInvoice(inv);
				document.title = `Faktur #${inv.number} - Cloudiva`;
			})
			.catch((error) => {
				setError(error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchPaymentList = (amount, method) => {
		setLoading(true);
		requestGetPaymentList(amount, method)
			.then((response) => {
				setPayments(response.data.data);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.message,
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const formatCurrency = (amount, currency = "IDR") => {
		return new Intl.NumberFormat("id-ID", {
			style: "currency",
			currency: currency,
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (date) => {
		return dayjs(date).format("DD MMMM YYYY");
	};

	const getStatusConfig = (status) => {
		const configs = {
			UNPAID: { color: "red", text: "BELUM LUNAS" },
			PARTIALLY_PAID: { color: "orange", text: "DICICIL" },
			PAID: { color: "green", text: "LUNAS" },
			CANCELLED: { color: "default", text: "DIBATALKAN" },
			EXPIRED: { color: "default", text: "KADALUARSA" },
			REFUNDED: { color: "default", text: "DIAJUKAN PENGEMBALIAN" },
			FAILED: { color: "default", text: "GAGAL" },
			VOIDED: { color: "default", text: "DIBATALKAN" },
		};
		return configs[status] || { color: "default", text: status };
	};

	const getTypeText = (type) => {
		const types = {
			NEW: "Pesanan Baru",
			RENEWAL: "Perpanjangan",
		};
		return types[type] || type;
	};

	const getItemDescription = (item) => {
		if (item.type === "INSTANCE" && item.instance) {
			return {
				title: `${item.instance.service?.name || "Layanan"} - ${
					item.instance.plan?.name || "Paket"
				}`,
				subtitle: `Instance: ${item.instance.number || "N/A"}`,
			};
		}
		if (item.type === "DOMAIN" && item.domain) {
			return {
				title: `Registrasi Domain - ${item.domain.name}`,
				subtitle: `Ekstensi: ${
					item.domain.extension?.name || item.domain.extension?.code || "N/A"
				} | Tipe: ${item.type}`,
			};
		}
		return {
			title: "Item Layanan",
			subtitle: `Tipe: ${item.type}`,
		};
	};

	const itemColumns = [
		{
			title: "Deskripsi",
			dataIndex: "description",
			key: "description",
			render: (_, record) => {
				const desc = getItemDescription(record);
				return (
					<div>
						<Text strong>{desc.title}</Text>
						<br />
						<Text type="secondary">{desc.subtitle}</Text>
					</div>
				);
			},
		},
		{
			title: "Jumlah",
			dataIndex: "quantity",
			key: "quantity",
			align: "center",
		},
		{
			title: "Harga Satuan",
			dataIndex: "unitSubtotal",
			key: "unitSubtotal",
			align: "right",
			render: (amount) => formatCurrency(amount, invoice?.currency),
		},
		{
			title: "Total",
			dataIndex: "lineTotal",
			key: "lineTotal",
			align: "right",
			render: (amount) => <Text strong>{formatCurrency(amount, invoice?.currency)}</Text>,
		},
	];

	const handlePaymentSelect = (payment) => {
		setSelectedPayment(payment);
	};

	const handlePaymentProceed = () => {
		// Check if there's already an active payment
		if (invoice?.payment && !isPaymentExpired) {
			notification.warning({
				message: "Pembayaran Aktif",
				description:
					"Anda memiliki pembayaran aktif yang belum kedaluwarsa. Silakan selesaikan pembayaran tersebut terlebih dahulu.",
			});
			return;
		}

		if (selectedPayment && invoice) {
			requestPayInvoiceWithToken(token, {
				method: selectedPayment.method,
				channel: selectedPayment.channel,
			})
				.then((response) => {
					message.success(
						"Pembayaran berhasil diproses. Anda akan diarahkan ke halaman pembayaran."
					);
					const data = response.data.data;
					const { invoice, transaction } = data;
					const paymentUrl = transaction?.metadata?.paymentUrl;

					if (paymentUrl) {
						window.open(paymentUrl, "_blank");
					}
					setPaymentDrawerVisible(false);
					setSelectedPayment(null); // Clear selection after creating payment
					// Fetch the updated invoice details
					fetchInvoiceWithToken();
				})
				.catch((error) => {
					notification.error({
						message: "Pembayaran Gagal",
						description: error.message || "Terjadi kesalahan saat memproses pembayaran.",
					});
				});
		}
	};

	const calculateTotalWithFee = (baseAmount, fee) => {
		return baseAmount + fee;
	};

	const hasActivePayment = () => {
		return invoice?.payment && !isPaymentExpired && timeRemaining;
	};

	const getPaymentStatusText = () => {
		if (!invoice?.payment) return null;

		if (isPaymentExpired) {
			return "Pembayaran Kedaluwarsa";
		}

		if (timeRemaining) {
			return `Menunggu Pembayaran via ${invoice?.payment?.channel.replace("_", " ") || "Unknown"}`;
		}

		return "Pembayaran Aktif";
	};

	const formatTimeRemaining = () => {
		if (!timeRemaining) return "";

		const { hours, minutes, seconds } = timeRemaining;
		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
			.toString()
			.padStart(2, "0")}`;
	};

	const openPaymentUrl = () => {
		if (invoice?.payment?.metadata?.paymentUrl) {
			window.open(invoice.payment.metadata.paymentUrl, "_blank");
		}
	};

	if (loading) {
		return (
			<Card>
				<Spin size="large" tip="Memuat faktur...">
					<div style={{ minHeight: 200 }} />
				</Spin>
			</Card>
		);
	}

	if (error || !invoice) {
		return (
			// <Result
			// 	status="404"
			// 	title="Faktur Tidak Ditemukan"
			// 	subTitle="Faktur yang diminta tidak dapat ditemukan atau sudah tidak tersedia lagi."
			// />
			<div
				style={{
					height: "100vh",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<Empty
					description={
						<span>Faktur yang diminta tidak dapat ditemukan atau sudah tidak tersedia lagi.</span>
					}
				/>
			</div>
		);
	}

	const statusConfig = getStatusConfig(invoice.status);

	return (
		<div
			style={{
				backgroundColor: "#f9f9f9",
				position: "relative",
				minHeight: "100vh",
				paddingBottom: "80px", // Space for floating button
			}}>
			<div
				style={{
					padding: "12px", // Reduced padding for mobile
					backgroundColor: "#fff",
					maxWidth: "900px",
					margin: "0 auto",
				}}>
				{/* Header */}
				<Card>
					<div style={{ textAlign: "center" }}>
						<img
							src="/favicon-96x96.png"
							alt="Cloudiva Logo"
							style={{ height: "96px", marginBottom: "8px" }}
						/>
						<br />
						<Title level={2} style={{ marginBottom: "8px", fontSize: "20px" }}>
							Faktur #{invoice.number}
						</Title>
						<Text type="secondary">Cloudiva by Codeiva</Text>
					</div>
				</Card>

				<Divider />

				{/* Invoice Information */}
				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Card
							title={
								<Space>
									<InfoCircleOutlined />
									Informasi Faktur
								</Space>
							}>
							<Descriptions
								column={{ xs: 1, sm: 1, md: 2 }}
								bordered
								size="small"
								style={{ width: "100%", overflowX: "auto" }}
								labelStyle={{ whiteSpace: "nowrap" }}>
								<Descriptions.Item label="Nomor Faktur">
									<Text strong>{invoice.number}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Tipe">{getTypeText(invoice.type)}</Descriptions.Item>
								<Descriptions.Item label="Tanggal Terbit">
									<Space size="small">
										<CalendarOutlined />
										<Text style={{ fontSize: "14px" }}>{formatDate(invoice.issuedAt)}</Text>
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label="Tanggal Jatuh Tempo">
									<Space size="small">
										<CalendarOutlined />
										<Text style={{ fontSize: "14px" }}>{formatDate(invoice.expiredAt)}</Text>
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label="Status" span={{ xs: 2, sm: 1 }}>
									<Tag color={statusConfig.color}>{statusConfig.text}</Tag>
								</Descriptions.Item>
							</Descriptions>
						</Card>
					</Col>

					<Col xs={24} sm={12}>
						<Card title={<Space>Ditagih Oleh</Space>}>
							<div>
								<Title level={5} style={{ marginBottom: "8px" }}>
									CV Codeiva Lentera Indonesia
								</Title>
								<div style={{ lineHeight: "1.5" }}>
									<Text style={{ fontSize: "14px" }}>Sumberrejo</Text>
									<br />
									<Text style={{ fontSize: "14px" }}>Bojonegoro, 62191</Text>
									<br />
									<Text style={{ fontSize: "14px" }}>Jawa Timur, Indonesia</Text>
								</div>
							</div>
						</Card>
					</Col>

					<Col xs={24} sm={12}>
						<Card title={<Space>Ditagih Kepada</Space>}>
							<div>
								<Title level={5} style={{ marginBottom: "8px" }}>
									{invoice.billingAddress.name}
								</Title>
								{invoice.billingAddress && (
									<div style={{ lineHeight: "1.5" }}>
										<Text style={{ fontSize: "14px" }}>{invoice.billingAddress.address}</Text>
										<br />
										<Text style={{ fontSize: "14px" }}>
											{invoice.billingAddress.city} {invoice.billingAddress.postalCode}
										</Text>
										<br />
										<Text style={{ fontSize: "14px" }}>
											{invoice.billingAddress.state}, {invoice.billingAddress.country}
										</Text>
									</div>
								)}
							</div>
						</Card>
					</Col>

					<Col xs={24}>
						{/* Items Table */}
						<Card title="Item">
							<Table
								columns={itemColumns}
								dataSource={invoice.items}
								rowKey="id"
								pagination={false}
								bordered
								scroll={{ x: 600 }} // Enable horizontal scroll on mobile
								size="small" // Make table more compact on mobile
							/>
						</Card>
					</Col>

					<Col xs={24}>
						{/* Totals */}
						<Card title="Ringkasan">
							<Row justify={{ xs: "center", sm: "end" }}>
								<Col xs={24} sm={16} md={12}>
									<Descriptions column={1} bordered size="small">
										<Descriptions.Item label="Subtotal">
											{formatCurrency(invoice.subtotal, invoice.currency)}
										</Descriptions.Item>

										{invoice.taxTotal > 0 && (
											<Descriptions.Item label="Pajak (PPN 11%)">
												{formatCurrency(invoice.taxTotal, invoice.currency)}
											</Descriptions.Item>
										)}

										{invoice.discountTotal > 0 && (
											<Descriptions.Item label="Diskon">
												-{formatCurrency(invoice.discountTotal, invoice.currency)}
											</Descriptions.Item>
										)}

										{invoice.feeTotal > 0 && (
											<Descriptions.Item label="Biaya">
												{formatCurrency(invoice.feeTotal, invoice.currency)}
											</Descriptions.Item>
										)}

										<Descriptions.Item label="Total">
											<Text strong type="success" style={{ fontSize: "16px" }}>
												{formatCurrency(invoice.total, invoice.currency)}
											</Text>
										</Descriptions.Item>
									</Descriptions>
								</Col>
							</Row>

							{/* Link Views Info */}
							{invoice.linkViews !== undefined && (
								<Alert
									message={
										<Space>
											<EyeOutlined />
											<Text style={{ fontSize: "14px" }}>
												Tampilan tautan: {invoice.linkViews}
												{invoice.linkMaxViews > 0 && ` / ${invoice.linkMaxViews}`}
											</Text>
										</Space>
									}
									type="info"
									style={{ marginTop: 16 }}
								/>
							)}
						</Card>
					</Col>
				</Row>

				<Divider />

				{/* Footer */}
				<Card>
					<Alert
						// message="Tampilan Faktur Publik"
						description={
							<div style={{ textAlign: "center" }}>
								<Text style={{ fontSize: "14px" }}>
									Ini adalah tampilan publik dari faktur. Untuk melihat detail lebih lengkap,
									silakan masuk ke akun Anda.
								</Text>
								<br />
								<Button
									type="dashed"
									icon={<ExportOutlined />}
									href="https://app.cloudiva.codeiva.com/login"
									style={{ marginTop: 8 }}
									target="_blank"
									size="small">
									Login ke Panel Pengguna
								</Button>
							</div>
						}
						type="info"
						// showIcon
					/>
				</Card>
			</div>

			{/* Payment Drawer */}
			<Drawer
				title="Pilih Metode Pembayaran"
				placement="bottom"
				onClose={() => setPaymentDrawerVisible(false)}
				visible={paymentDrawerVisible}
				height="80%" // Increased for mobile
				styles={{
					body: {
						padding: "12px",
						width: "100%",
						maxWidth: "900px",
						margin: "0 auto",
					},
				}}>
				{/* Show active payment alert */}
				{hasActivePayment() && (
					<Alert
						message={getPaymentStatusText()}
						description={
							<div>
								<Text>Pembayaran Anda masih aktif dan akan kedaluwarsa dalam:</Text>
								<br />
								<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
									{formatTimeRemaining()}
								</Text>
								<br />
								<Text type="secondary" style={{ fontSize: "12px" }}>
									Silakan selesaikan pembayaran yang sedang aktif atau tunggu hingga kedaluwarsa
									untuk membuat pembayaran baru.
								</Text>
								{invoice?.payment?.metadata?.paymentUrl && (
									<div style={{ marginTop: 12 }}>
										<Button
											type="primary"
											size="small"
											icon={<LinkOutlined />}
											onClick={openPaymentUrl}>
											Buka Halaman Pembayaran
										</Button>
									</div>
								)}
							</div>
						}
						type="warning"
						style={{ marginBottom: 16 }}
						showIcon
					/>
				)}

				{/* Show expired payment alert */}
				{invoice?.payment && isPaymentExpired && (
					<Alert
						message="Pembayaran Kedaluwarsa"
						description="Pembayaran sebelumnya telah kedaluwarsa. Anda dapat membuat pembayaran baru."
						type="info"
						style={{ marginBottom: 16 }}
						showIcon
					/>
				)}

				<div style={{ marginBottom: 16 }}>
					<Card size="small">
						<Row justify="space-between" align="middle" style={{ marginBottom: "8px" }}>
							<Col>
								<Text strong style={{ fontSize: "14px" }}>
									Total Faktur:
								</Text>
							</Col>
							<Col>
								<Text strong style={{ fontSize: "14px" }}>
									{formatCurrency(invoice?.total || 0, invoice?.currency)}
								</Text>
							</Col>
						</Row>
						{selectedPayment && !hasActivePayment() && (
							<>
								<Divider style={{ margin: "8px 0" }} />
								<Row justify="space-between" align="middle" style={{ marginBottom: "8px" }}>
									<Col flex="auto">
										<Text style={{ fontSize: "12px" }}>
											Biaya Transaksi ({selectedPayment.name}):
										</Text>
									</Col>
									<Col flex="none">
										<Text style={{ fontSize: "12px" }}>
											{selectedPayment.fee > 0
												? `+${formatCurrency(selectedPayment.fee, invoice?.currency)}`
												: "Gratis"}
										</Text>
									</Col>
								</Row>
								<Divider style={{ margin: "8px 0" }} />
								<Row justify="space-between" align="middle">
									<Col>
										<Text strong style={{ fontSize: "14px" }}>
											Total Pembayaran:
										</Text>
									</Col>
									<Col>
										<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
											{formatCurrency(
												calculateTotalWithFee(invoice?.total || 0, selectedPayment.fee),
												invoice?.currency
											)}
										</Text>
									</Col>
								</Row>
							</>
						)}
					</Card>
				</div>

				<Card title="Metode Pembayaran" size="small">
					<List
						dataSource={payments}
						renderItem={(payment) => (
							<List.Item
								style={{
									cursor: hasActivePayment() ? "not-allowed" : "pointer",
									border:
										selectedPayment?.channel === payment.channel
											? "2px solid #1890ff"
											: "1px solid #f0f0f0",
									borderRadius: "8px",
									marginBottom: "8px",
									padding: "8px",
									backgroundColor: hasActivePayment()
										? "#f5f5f5"
										: selectedPayment?.channel === payment.channel
										? "#f6ffed"
										: "white",
									opacity: hasActivePayment() ? 0.5 : 1,
								}}
								onClick={() => !hasActivePayment() && handlePaymentSelect(payment)}>
								<List.Item.Meta
									avatar={<Avatar src={payment.image} size={32} shape="square" />}
									title={
										<Space>
											<Text strong style={{ fontSize: "14px" }}>
												{payment.name}
											</Text>
											{selectedPayment?.channel === payment.channel && !hasActivePayment() && (
												<Tag color="blue" size="small">
													Dipilih
												</Tag>
											)}
											{hasActivePayment() && (
												<Tag color="default" size="small">
													Dinonaktifkan
												</Tag>
											)}
										</Space>
									}
									description={
										<Text type="secondary" style={{ fontSize: "12px" }}>
											Biaya Transaksi:{" "}
											{payment.fee > 0 ? formatCurrency(payment.fee, invoice?.currency) : "Gratis"}
										</Text>
									}
								/>
							</List.Item>
						)}
					/>
				</Card>

				{selectedPayment && !hasActivePayment() && (
					<div
						style={{
							position: "sticky",
							bottom: 0,
						}}>
						<Button
							type="primary"
							size="large"
							block
							icon={<PayCircleOutlined />}
							onClick={handlePaymentProceed}
							style={{ fontSize: "14px" }}>
							Bayar{" "}
							{formatCurrency(
								calculateTotalWithFee(invoice?.total || 0, selectedPayment.fee),
								invoice?.currency
							)}
						</Button>
					</div>
				)}
			</Drawer>

			{/* Floating Payment Button */}
			{invoice.status === "UNPAID" && (
				<div
					style={{
						position: "fixed",
						bottom: 24,
						left: "50%",
						transform: "translateX(-50%)",
						zIndex: 1000,
					}}>
					{hasActivePayment() && (
						<Card
							size="small"
							style={{
								marginBottom: 12,
								textAlign: "center",
								minWidth: 280,
								boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
							}}>
							<Text type="secondary" style={{ fontSize: "12px" }}>
								{getPaymentStatusText()}
							</Text>
							<br />
							<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
								{formatTimeRemaining()}
							</Text>
							{invoice?.payment?.metadata?.paymentUrl && (
								<div style={{ marginTop: 8 }}>
									<Button
										type="link"
										size="small"
										icon={<LinkOutlined />}
										onClick={openPaymentUrl}
										style={{ fontSize: "12px" }}>
										Buka Pembayaran
									</Button>
								</div>
							)}
						</Card>
					)}

					<Button
						type="primary"
						size="large"
						icon={hasActivePayment() ? <LinkOutlined /> : <PayCircleOutlined />}
						onClick={hasActivePayment() ? openPaymentUrl : () => setPaymentDrawerVisible(true)}
						disabled={hasActivePayment() && !invoice?.payment?.metadata?.paymentUrl}
						style={{
							borderRadius: "12px",
							height: "48px",
							fontSize: "14px",
							boxShadow: "0 4px 12px rgba(24, 144, 255, 0.4)",
							minWidth: 280,
						}}>
						{hasActivePayment()
							? invoice?.payment?.metadata?.paymentUrl
								? `Buka Pembayaran - ${formatTimeRemaining()}`
								: `Menunggu Pembayaran - ${formatTimeRemaining()}`
							: `Bayar ${formatCurrency(invoice?.total || 0, invoice?.currency)}`}
					</Button>
				</div>
			)}
		</div>
	);
}

export default PublicInvoicePage;
